import React, { useState } from 'react';
import {
  Dialog,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { Eye, EyeOff, ShieldCheck, Lock, ArrowLeft, Fingerprint, KeyRound, ScanFace } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ActionButton from '../../components/ActionButton';

const circuitLines = (
  <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.15 }}>
    {/* Horizontal lines */}
    <line x1="0" y1="20" x2="60" y2="20" stroke="#6C63FF" strokeWidth="1" />
    <circle cx="60" cy="20" r="2.5" fill="#6C63FF" />
    <line x1="60" y1="20" x2="60" y2="45" stroke="#6C63FF" strokeWidth="1" />
    <circle cx="60" cy="45" r="2" fill="#6C63FF" />

    <line x1="0" y1="70" x2="35" y2="70" stroke="#10B981" strokeWidth="1" />
    <circle cx="35" cy="70" r="2" fill="#10B981" />
    <line x1="35" y1="70" x2="35" y2="55" stroke="#10B981" strokeWidth="1" />

    {/* Right side */}
    <line x1="100%" y1="25" x2="calc(100% - 55px)" y2="25" stroke="#6C63FF" strokeWidth="1" />
    <circle cx="calc(100% - 55px)" cy="25" r="2.5" fill="#6C63FF" />
    <line x1="calc(100% - 55px)" y1="25" x2="calc(100% - 55px)" y2="50" stroke="#6C63FF" strokeWidth="1" />

    <line x1="100%" y1="65" x2="calc(100% - 40px)" y2="65" stroke="#10B981" strokeWidth="1" />
    <circle cx="calc(100% - 40px)" cy="65" r="2" fill="#10B981" />

    {/* Bottom lines */}
    <line x1="20" y1="100%" x2="20" y2="calc(100% - 30px)" stroke="#6C63FF" strokeWidth="1" />
    <circle cx="20" cy="calc(100% - 30px)" r="2" fill="#6C63FF" />
    <line x1="20" y1="calc(100% - 30px)" x2="50" y2="calc(100% - 30px)" stroke="#6C63FF" strokeWidth="1" />

    <line x1="calc(100% - 25px)" y1="100%" x2="calc(100% - 25px)" y2="calc(100% - 35px)" stroke="#10B981" strokeWidth="1" />
    <circle cx="calc(100% - 25px)" cy="calc(100% - 35px)" r="2" fill="#10B981" />
    <line x1="calc(100% - 25px)" y1="calc(100% - 35px)" x2="calc(100% - 60px)" y2="calc(100% - 35px)" stroke="#10B981" strokeWidth="1" />
    <circle cx="calc(100% - 60px)" cy="calc(100% - 35px)" r="2.5" fill="#10B981" />
  </svg>
);

export default function UserDetailsPasswordModal({
  open,
  onPasswordVerified,
  loading,
  error
}) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (password.trim()) {
      onPasswordVerified(password);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '2px',
      backgroundColor: 'var(--bg-elevated)',
      '& fieldset': { borderColor: 'var(--border)' },
      '&:hover fieldset': { borderColor: 'var(--primary)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--primary)' },
    },
    '& .MuiInputLabel-root': {
      color: 'var(--text-secondary)',
      '&.Mui-focused': { color: 'var(--primary)' },
    },
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return false;
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'var(--bg-paper)',
          border: '1px solid var(--border)',
          borderRadius: '2px',
          overflow: 'hidden',
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          }
        }
      }}
    >
      {/* Circuit board background */}
      {circuitLines}

      {/* Floating security icons */}
      <Box sx={{ position: 'absolute', top: 16, right: 18, opacity: 0.15, pointerEvents: 'none' }}>
        <Fingerprint size={32} color="#6C63FF" />
      </Box>
      <Box sx={{ position: 'absolute', bottom: 18, left: 18, opacity: 0.15, pointerEvents: 'none' }}>
        <KeyRound size={24} color="#10B981" />
      </Box>
      <Box sx={{ position: 'absolute', top: 50, left: 22, opacity: 0.12, pointerEvents: 'none' }}>
        <ScanFace size={20} color="#6C63FF" />
      </Box>
      <Box sx={{ position: 'absolute', bottom: 50, right: 22, opacity: 0.12, pointerEvents: 'none' }}>
        <Lock size={18} color="#10B981" />
      </Box>

      <Box sx={{ px: 4, py: 3, position: 'relative', zIndex: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
          <Box sx={{
            width: 38,
            height: 38,
            borderRadius: '2px',
            backgroundColor: 'rgba(108, 99, 255, 0.12)',
            border: '1px solid rgba(108, 99, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ShieldCheck size={18} color="#6C63FF" />
          </Box>
          <Box>
            <Typography sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
              Access User Details
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: '0.75rem' }}>
              Enter your admin password to continue
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: '2px',
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#EF4444',
              '& .MuiAlert-icon': { color: '#EF4444' },
            }}
          >
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          label="Admin Password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          variant="outlined"
          size="small"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          sx={inputSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock size={16} color="#64748B" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={loading}
                  size="small"
                  sx={{ color: '#64748B' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2.5}>
          <ActionButton
            variant="info"
            onClick={() => navigate('/')}
            sx={{ gap: 0.5, px: 2, py: 0.8, fontSize: '0.8rem', borderRadius: '2px' }}
          >
            <ArrowLeft size={14} /> Go to Dashboard
          </ActionButton>
          <ActionButton
            variant="success"
            onClick={handleSubmit}
            disabled={!password.trim() || loading}
            sx={{ gap: 1, px: 3, py: 0.8, borderRadius: '2px' }}
          >
            {loading ? (
              <CircularProgress size={16} sx={{ color: '#10B981' }} />
            ) : (
              <>
                <ShieldCheck size={15} /> Verify
              </>
            )}
          </ActionButton>
        </Box>
      </Box>
    </Dialog>
  );
}
